import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import { TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import {
  AppWindow,
  ChevronRight,
  MousePointerClick,
  Type as TypeIcon,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useReducedMotion } from 'motion/react';
import { memo, useState, type CSSProperties } from 'react';
import { useIntl } from 'react-intl';

import { computerUseMessages } from '../../../data/projects/components/ComputerUseTaskBlock.status.utils';
import type { DelegateTraceEntry } from '../../../data/projects/lib/computer-use-trace.types';
import { workingBlockMessages } from '../../../data/projects/timeline/working-block/workingBlock.messages';
import {
  formatComputerUseElapsed,
  selectComputerUseActivityEntries,
} from './ComputerUsePlanCard.utils';

export interface ComputerUseWorkingBlockProps {
  trace: readonly DelegateTraceEntry[] | undefined;
  running: boolean;
  defaultCollapsed?: boolean;
  elapsedSeconds: number | null;
}

const LIVE_TEXT_SHIMMER_STYLE: CSSProperties = {
  maskImage:
    'linear-gradient(90deg, rgb(0, 0, 0) 0%, rgb(0, 0, 0) 35%, rgba(0, 0, 0, 0.35) 50%, rgb(0, 0, 0) 65%, rgb(0, 0, 0) 100%)',
  maskSize: '200% 100%',
  WebkitMaskImage:
    'linear-gradient(90deg, rgb(0, 0, 0) 0%, rgb(0, 0, 0) 35%, rgba(0, 0, 0, 0.35) 50%, rgb(0, 0, 0) 65%, rgb(0, 0, 0) 100%)',
  WebkitMaskSize: '200% 100%',
  animation: 'ptb-text-shimmer 2s ease-in-out infinite',
};

export const ComputerUseWorkingBlock = ({
  trace,
  running,
  defaultCollapsed = false,
  elapsedSeconds,
}: ComputerUseWorkingBlockProps): JSX.Element => {
  const intl = useIntl();
  const reduceMotion = useReducedMotion();
  const [expanded, setExpanded] = useState(!defaultCollapsed);

  const entries = selectComputerUseActivityEntries(trace);
  const header = running
    ? intl.formatMessage(computerUseMessages.workingFor, {
        elapsed: formatComputerUseElapsed(elapsedSeconds ?? 0),
      })
    : elapsedSeconds === null || elapsedSeconds === 0
      ? intl.formatMessage(workingBlockMessages.thought)
      : intl.formatMessage(computerUseMessages.workedFor, {
          elapsed: formatComputerUseElapsed(elapsedSeconds),
        });

  const headerRow = (
    <span className="flex items-center gap-2">
      <TypographyP className="text-text-secondary">{header}</TypographyP>
      <Badge color="neutral" size="sm" className="rounded-sm font-normal">
        {intl.formatMessage(computerUseMessages.familyLabel)}
      </Badge>
    </span>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="computer-use-working-block">
      {entries.length > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={() => setExpanded((open) => !open)}
          className="h-auto justify-start gap-1.5 self-start bg-transparent p-0 text-start font-normal hover:bg-transparent active:bg-transparent aria-expanded:bg-transparent"
          aria-expanded={expanded}
        >
          {headerRow}
          <ChevronRight
            aria-hidden
            className={cn(
              'size-4 shrink-0 text-text-secondary rtl:-scale-x-100',
              'motion-safe:transition-transform motion-safe:duration-300 motion-reduce:transition-none',
              expanded && 'rotate-90'
            )}
          />
        </Button>
      ) : (
        headerRow
      )}

      {expanded && entries.length > 0 && (
        <ol className="flex flex-col gap-3">
          {entries.map((entry, index) => (
            <ActivityRow
              key={entry.id}
              entry={entry}
              shimmer={running && !reduceMotion && index === entries.length - 1}
            />
          ))}
        </ol>
      )}
    </div>
  );
};

const ActivityRow = memo(function ActivityRow({
  entry,
  shimmer,
}: {
  entry: DelegateTraceEntry;
  shimmer: boolean;
}): JSX.Element {
  const Icon = activityRowIcon(entry);

  // Prose stays full-width so it does not look like another physical action.
  if (!Icon) {
    return (
      <li>
        <TypographyP2
          className="whitespace-pre-wrap break-words text-text-tertiary [overflow-wrap:anywhere]"
          style={shimmer ? LIVE_TEXT_SHIMMER_STYLE : undefined}
        >
          {entry.label}
        </TypographyP2>
      </li>
    );
  }

  return (
    <li
      className={cn(
        'flex items-center gap-2',
        entry.isError ? 'text-validation-destructive' : 'text-text-tertiary'
      )}
      data-error={entry.isError ? 'true' : undefined}
    >
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <TypographyP2
        className="min-w-0 break-words [overflow-wrap:anywhere]"
        style={shimmer ? LIVE_TEXT_SHIMMER_STYLE : undefined}
      >
        {entry.label}
      </TypographyP2>
    </li>
  );
});

// Translate tool names into physical actions rather than exposing implementation terms.
const activityRowIcon = (entry: DelegateTraceEntry): LucideIcon | undefined => {
  if (entry.isError) return X;
  if (entry.kind !== 'tool_call') return undefined;
  const tool = entry.toolName?.toLowerCase() ?? '';
  if (tool.includes('type') || tool.includes('key')) return TypeIcon;
  if (tool.includes('open') || tool.includes('launch') || tool.includes('app')) {
    return AppWindow;
  }
  return MousePointerClick;
};
